import {
  CheckCircleIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import { getConnectDetails } from "../../services/api";
import { StripeAccountStatusDto } from "../../types/types";

interface StripeAccountDetailsModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const StripeAccountDetailsModal: React.FC<StripeAccountDetailsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation("marketPlaceCards");
  const [isAccountDetailsOpen, setIsAccountDetailsOpen] =
    React.useState(false);
  const [accountDetails, setAccountDetails] =
    React.useState<StripeAccountStatusDto | null>(null);
  const [accountDetailsLoading, setAccountDetailsLoading] =
    React.useState(false);
  const [accountDetailsError, setAccountDetailsError] = React.useState("");

  const isControlled = isOpen !== undefined;
  const isModalOpen = isControlled ? Boolean(isOpen) : isAccountDetailsOpen;

  const loadAccountDetails = async () => {
    setAccountDetailsLoading(true);
    setAccountDetailsError("");

    try {
      const details = await getConnectDetails();
      setAccountDetails(details);
    } catch (error: any) {
      setAccountDetails(null);
      setAccountDetailsError(
        error?.response?.data?.message ||
          error?.message ||
          t("failed_to_load_account_details")
      );
    } finally {
      setAccountDetailsLoading(false);
    }
  };

  const handleGetAccountDetails = () => {
    setIsAccountDetailsOpen(true);
    loadAccountDetails();
  };

  const handleCloseModal = () => {
    setIsAccountDetailsOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (isOpen) {
      loadAccountDetails();
    }
  }, [isOpen]);

  const formatTitleCase = (value: string) =>
    value
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const formatValue = (value: string | boolean | undefined | null) => {
    if (typeof value === "boolean") {
      return value ? t("yes") : t("no");
    }

    return value ? formatTitleCase(value) : "-";
  };

  const hasDisplayValue = (value: string | undefined | null) =>
    typeof value === "string" && value.trim().length > 0;

  const formatBankAccountLast4 = (last4?: string | null) =>
    hasDisplayValue(last4) ? `**************${last4}` : "";

  const accountInfoRows = accountDetails
    ? [
        {
          label: t("bank_name"),
          value: hasDisplayValue(accountDetails.bankName)
            ? formatValue(accountDetails.bankName)
            : "",
        },
        {
          label: t("account_type"),
          value: hasDisplayValue(accountDetails.accountType)
            ? formatValue(accountDetails.accountType)
            : "",
        },
        {
          label: t("bank_account_last4"),
          value: formatBankAccountLast4(accountDetails.bankAccountLast4),
          isSensitive: true,
        },
        {
          label: t("verification_sub_status"),
          value: hasDisplayValue(accountDetails.verificationSubStatus)
            ? formatValue(accountDetails.verificationSubStatus)
            : "",
        },
        {
          label: t("disabled_reason"),
          value: hasDisplayValue(accountDetails.disabledReason)
            ? formatValue(accountDetails.disabledReason)
            : "",
        },
      ].filter((row) => hasDisplayValue(row.value))
    : [];

  const capabilityRows = accountDetails
    ? [
        { label: t("payouts_enabled"), value: accountDetails.payoutsEnabled },
        { label: t("charges_enabled"), value: accountDetails.chargesEnabled },
        {
          label: t("details_submitted"),
          value: accountDetails.detailsSubmitted,
        },
      ]
    : [];

  const requirementSections = accountDetails
    ? [
        {
          label: t("requirements_currently_due"),
          values: accountDetails.requirementsCurrentlyDue || [],
        },
        {
          label: t("requirements_eventually_due"),
          values: accountDetails.requirementsEventuallyDue || [],
        },
        {
          label: t("requirements_past_due"),
          values: accountDetails.requirementsPastDue || [],
        },
        {
          label: t("requirement_errors"),
          values: accountDetails.requirementErrors || [],
        },
      ]
    : [];

  const hasOpenRequirements = requirementSections.some(
    (section) => section.values.length > 0
  );

  return (
    <>
      {!isControlled && (
        <Button
          variant="bordered"
          className="h-8 px-4 rounded-2xl text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-sm"
          size="sm"
          onPress={handleGetAccountDetails}
          isLoading={accountDetailsLoading}
          isDisabled={accountDetailsLoading}
        >
          {t("get_account_details")}
        </Button>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="2xl"
        scrollBehavior="inside"
        classNames={{
          backdrop: "bg-black/45",
          base: "border border-gray-200 shadow-2xl",
          body: "px-6 py-0 max-h-[78vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
          header: "px-6 pb-2 pt-4",
          footer: "border-t border-gray-100 px-6 py-3",
        }}
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <CreditCardIcon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {t("account_details")}
                </h3>
                <p className="mt-1 text-xs font-normal text-gray-500">
                  {t("account_details_description")}
                </p>
              </div>
            </div>
          </ModalHeader>
          <ModalBody>
            {accountDetailsLoading ? (
              <div className="flex min-h-52 items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-9 w-9 animate-spin rounded-full border-[3px] border-blue-100 border-t-blue-600" />
                  <p className="text-sm font-medium text-gray-600">
                    {t("loading")}
                  </p>
                </div>
              </div>
            ) : accountDetailsError ? (
              <div className="mb-5 flex gap-3 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-700">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <span>{accountDetailsError}</span>
              </div>
            ) : accountDetails ? (
              <div className="space-y-4 pb-4">
                <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-blue-600">
                        {t("status")}
                      </p>
                      <p className="mt-1 text-2xl font-semibold text-gray-900">
                        {formatValue(accountDetails.status)}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
                        hasOpenRequirements
                          ? "bg-amber-100 text-amber-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {hasOpenRequirements ? (
                        <ExclamationTriangleIcon className="h-4 w-4" />
                      ) : (
                        <CheckCircleIcon className="h-4 w-4" />
                      )}
                      {hasOpenRequirements
                        ? t("verification_pending")
                        : t("no_requirements")}
                    </span>
                  </div>
                </div>

                <section>
                  <p className="mb-2 text-sm font-semibold text-gray-800">
                    {t("payment_capabilities")}
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {capabilityRows.map((row) => {
                      const isEnabled = Boolean(row.value);

                      return (
                        <div
                          key={row.label}
                          className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white px-3 py-3"
                        >
                          {isEnabled ? (
                            <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-500" />
                          ) : (
                            <XCircleIcon className="h-5 w-5 shrink-0 text-gray-400" />
                          )}
                          <div className="min-w-0">
                            <p className="truncate text-xs text-gray-500">
                              {row.label}
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatValue(row.value)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section>
                  <p className="mb-2 text-sm font-semibold text-gray-800">
                    {t("account_information")}
                  </p>
                  <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
                    {accountInfoRows.map((row) => (
                      <div
                        key={row.label}
                        className="flex flex-col gap-1 border-b border-gray-100 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                      >
                        <p className="text-xs font-medium text-gray-500">
                          {row.label}
                        </p>
                        <p
                          className={`break-words text-sm font-semibold leading-5 text-gray-900 sm:text-right ${
                            row.isSensitive ? "font-mono tracking-wide" : ""
                          }`}
                        >
                          {row.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {hasOpenRequirements && (
                  <section>
                    <p className="mb-2 text-sm font-semibold text-gray-800">
                      {t("verification_requirements")}
                    </p>
                    <div className="space-y-2">
                      {requirementSections
                        .filter((section) => section.values.length > 0)
                        .map((section) => (
                          <div
                            key={section.label}
                            className="rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2.5"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs font-semibold text-gray-700">
                                {section.label}
                              </p>
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                {section.values.length}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {section.values.map((value) => (
                                <span
                                  key={value}
                                  className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
                                >
                                  {formatTitleCase(value)}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="flex min-h-40 items-center justify-center text-sm text-gray-400">
                {t("no_account_details")}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <div className="flex w-full items-center justify-between gap-3">
              <p className="text-xs text-gray-400">
                {accountDetails ? t("account_details_footer") : ""}
              </p>
              <div className="flex items-center gap-2">
                {accountDetails?.onboardingUrl && (
                  <Button
                    color="primary"
                    size="sm"
                    onPress={() =>
                      window.open(
                        accountDetails.onboardingUrl!,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    {t("update_details")}
                  </Button>
                )}
                <Button
                  variant="light"
                  size="sm"
                  onPress={handleCloseModal}
                >
                  {t("close")}
                </Button>
              </div>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default StripeAccountDetailsModal;